import { SCHEMA_MAP } from "./schema";
import type { SettingKey, SettingsValues } from "./schema";
import { buildRegistry } from "./registry";
import { loadClientConfig } from "./configSource";
import { adapters } from "./persistence";
import type { RegistryEntry, SettingDef, SettingScope, ValueSettingDef } from "./types";

type PartialValues = Partial<SettingsValues>;
type Status = "idle" | "loading" | "ready" | "error";

class SettingsStore {
  /** 커밋 전 임시 값. 가장 높은 우선순위 */
  private draft: PartialValues = {};

  /** 저장된 값. draft → device → user → site → default 순으로 조회 */
  private layers: Record<SettingScope, PartialValues> = {
    site: {},
    user: {},
    device: {},
  };

  private entries: RegistryEntry[] = [];
  private registry: Record<string, RegistryEntry> = {};

  private keyListeners = new Map<string, Set<() => void>>();
  private metaListeners = new Set<() => void>();

  private _status: Status = "idle";

  /** useSyncExternalStore 스냅샷은 참조가 안정적이어야 함 */
  private dirtyKeysCache: string[] = [];

  // ────────────────────────────── 읽기

  getStatus = (): Status => this._status;
  getEntries = (): RegistryEntry[] => this.entries;

  get<K extends SettingKey>(key: K): SettingsValues[K] {
    if (key in this.draft) return this.draft[key] as SettingsValues[K];
    if (key in this.layers.device) return this.layers.device[key] as SettingsValues[K];
    if (key in this.layers.user) return this.layers.user[key] as SettingsValues[K];
    if (key in this.layers.site) return this.layers.site[key] as SettingsValues[K];
    return (SCHEMA_MAP[key] as ValueSettingDef).default as SettingsValues[K];
  }

  isAvailable = (key: string): boolean => Object.prototype.hasOwnProperty.call(this.registry, key);

  isLocked = (key: string): boolean => this.registry[key]?.locked ?? false;

  isDirty = (): boolean => this.dirtyKeysCache.length > 0;
  getDirtyKeys = (): string[] => this.dirtyKeysCache;

  /** 이 키가 커밋되지 않은 변경분을 갖고 있는가 (UI 표시용) */
  isModified = (key: string): boolean => Object.prototype.hasOwnProperty.call(this.draft, key);

  // ────────────────────────────── 쓰기 (draft에만)

  set<K extends SettingKey>(key: K, value: SettingsValues[K]): void {
    const entry: RegistryEntry | undefined = this.registry[key];
    if (!entry) throw new Error(`[settings] 사용할 수 없는 옵션: ${key}`);
    if (entry.locked) throw new Error(`[settings] 잠긴 옵션: ${key}`);

    const scope = (entry.def as ValueSettingDef).scope;
    if (scope === "site") {
      throw new Error(`[settings] site 옵션은 서버 설정입니다: ${key}`);
    }

    // 저장된 값과 같아지면 draft에서 제거 (되돌리면 dirty 해제)
    if (this.savedValue(key) === value) {
      delete this.draft[key];
    } else {
      this.draft[key] = value;
    }

    this.refreshDirty();
    this.notify(key);
    this.notifyMeta();
  }

  /** 개별 옵션을 상위 레이어 값으로 되돌림 */
  reset<K extends SettingKey>(key: K): void {
    const entry: RegistryEntry | undefined = this.registry[key];
    if (!entry) return;

    const scope = (entry.def as ValueSettingDef).scope;
    if (scope === "site") return;

    // 저장 레이어에 값이 있으면 삭제 신호(null)를 draft에 담아둠
    if (key in this.layers[scope]) {
      this.draft[key] = null as never;
    } else {
      delete this.draft[key];
    }

    this.refreshDirty();
    this.notify(key);
    this.notifyMeta();
  }

  /** 해당 키의 draft만 즉시 저장 (화면 설정처럼 저장 버튼 없이 반영하는 옵션용) */
  async saveNow<K extends SettingKey>(key: K): Promise<void> {
    if (!(key in this.draft)) return;
    const scope = (this.registry[key].def as ValueSettingDef).scope;
    const value = this.draft[key];
    await adapters[scope].saveMany({ [key]: value });

    if (this.draft[key] !== value) return; // 저장 중에 값이 또 바뀐 경우 최신 draft 유지
    if (value === null) delete this.layers[scope][key];
    else this.layers[scope][key] = value;
    delete this.draft[key];
    this.refreshDirty();
    this.notify(key);
    this.notifyMeta();
  }

  // ────────────────────────────── 커밋 / 취소

  getAllValues(scope: SettingScope): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    this.entries.forEach((e) => {
      if (e.def.type === "action") return;
      if (e.def.scope !== scope) return;
      out[e.key] = this.get(e.key as SettingKey);
    });
    return out;
  }

  /** 저장 성공 후 draft를 확정 */
  markSaved(): void {
    Object.assign(this.layers.user, this.draft);
    Object.assign(this.layers.device, this.draft);
    this.draft = {};
    this.refreshDirty();
    this.notifyAll();
  }

  discard(): void {
    const keys = Object.keys(this.draft);
    this.draft = {};
    this.refreshDirty();
    keys.forEach((k) => this.notify(k));
    this.notifyMeta();
  }

  // ────────────────────────────── 초기화

  async init(): Promise<void> {
    if (this._status === "loading" || this._status === "ready") return;
    this._status = "loading";
    this.notifyMeta();

    try {
      const config = await loadClientConfig();

      this.entries = buildRegistry(config);
      this.registry = {};
      this.entries.forEach((e) => {
        this.registry[e.key] = e;
      });

      this.layers.site = this.prune(config.settings?.defaults ?? {});
      this.layers.device = this.prune(await adapters.device.load());

      try {
        this.layers.user = this.prune(await adapters.user.load());
      } catch (e) {
        console.warn("[settings] user config 로드 실패 — 기본값 사용", e);
        this.layers.user = {};
      }

      this.draft = {};
      this.refreshDirty();

      this._status = "ready";
      this.notifyAll();
    } catch (e) {
      console.error("[settings] init 실패", e);
      this._status = "error";
      this.notifyMeta();
    }
  }

  retry(): void {
    if (this._status === "loading") return;
    this._status = "idle";
    void this.init();
  }

  /** 로그인 직후 호출 — user 레이어만 다시 읽음 */
  async reloadUser(): Promise<void> {
    try {
      this.layers.user = this.prune(await adapters.user.load());
      this.notifyAll();
    } catch (e) {
      console.warn("[settings] user config 갱신 실패", e);
    }
  }

  /** 로그아웃 시 호출 — 다음 사용자에게 값이 새지 않도록 */
  clearUser(): void {
    this.layers.user = {};
    this.draft = {};
    this.refreshDirty();
    this.notifyAll();
  }

  // ────────────────────────────── 구독

  subscribe = (key: string, fn: () => void): (() => void) => {
    let set = this.keyListeners.get(key);
    if (!set) this.keyListeners.set(key, (set = new Set()));
    set.add(fn);
    return () => {
      set!.delete(fn);
    };
  };

  subscribeMeta = (fn: () => void): (() => void) => {
    this.metaListeners.add(fn);
    return () => {
      this.metaListeners.delete(fn);
    };
  };

  // ────────────────────────────── 내부

  private notify(key: string) {
    this.keyListeners.get(key)?.forEach((f) => f());
  }

  private notifyMeta() {
    this.metaListeners.forEach((f) => f());
  }

  private notifyAll() {
    this.keyListeners.forEach((s) => s.forEach((f) => f()));
    this.notifyMeta();
  }

  private refreshDirty() {
    this.dirtyKeysCache = Object.keys(this.draft);
  }

  /** draft를 제외한, 실제 저장된 값 */
  private savedValue<K extends SettingKey>(key: K): SettingsValues[K] {
    if (key in this.layers.device) return this.layers.device[key] as SettingsValues[K];
    if (key in this.layers.user) return this.layers.user[key] as SettingsValues[K];
    if (key in this.layers.site) return this.layers.site[key] as SettingsValues[K];
    return (SCHEMA_MAP[key] as ValueSettingDef).default as SettingsValues[K];
  }

  /** 스키마에 없거나 타입이 맞지 않는 값 제거 */
  private prune(raw: Record<string, unknown>): PartialValues {
    const out: Record<string, unknown> = {};
    Object.entries(raw).forEach(([k, v]) => {
      const def: SettingDef | undefined = SCHEMA_MAP[k];
      if (!def || def.type === "action") return;
      if (def.type === "boolean" && typeof v !== "boolean") return;
      if (def.type === "number" && typeof v !== "number") return;
      if (def.type === "select" && !def.options.some((o) => o.value === v)) return;
      out[k] = v;
    });
    return out as PartialValues;
  }
}

export const settingsStore = new SettingsStore();
